import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Chip, Tooltip } from "@mui/material";
import CustomRiskTooltip from "./CustomRiskTooltip";
import CustomEvaluationHeader from "./CustomEvaluationHeader";
import CustomEvaluationValue from "./CustomEvaluationValue";
import { getFormattedDate } from "../../../utils/helper";
import { SampleFlagTooltip } from "./SampleFlagTooltip";
import LinkField from "../LinkField";

export const columns: GridColDef[] = [
  {
    field: "isSampled",
    headerName: "",
    flex: 1,
    headerAlign: "center",
    align: "center",
    sortable: false,
    headerClassName: "super-app-theme--header",
    renderCell: (params) =>
      params.value ? (
        <SampleFlagTooltip
          creatorEmail={params?.row?.creatorEmail}
          sampleId={params?.row?.sampleId}
        />
      ) : null,
  },
  {
    field: "sourceId",
    headerName: "Source ID",
    flex: 1,
    minWidth: 100,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "ocepLink",
    headerName: "Source Link",
    flex: 1,
    minWidth: 100,
    sortable: false,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    renderCell: (params) => (
      <LinkField
        url={params.value}
        tooltip={{ title: "Open in OCE-P" }}
        stopPropagation
      />
    ),
  },
  {
    field: "recordId",
    headerName: "Record ID",
    flex: 1,
    minWidth: 180,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "risk",
    headerName: "Risk",
    flex: 2,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    renderCell: (params) => (
      <Tooltip
        title={
          <CustomRiskTooltip
            aiRisks={params.value}
            humanRisks={
              params.value === "NR"
                ? params.row?.humanRisks?.map((item: string) => item.trim())
                : ""
            }
          />
        }
        arrow
      >
        <div data-testid={`risk-${params.id}`}>{params.value}</div>
      </Tooltip>
    ),
  },
  {
    field: "severity",
    headerName: "AI Risk Severity",
    flex: 1,
    minWidth: 200,
    headerAlign: "center",
    align: "center",
    sortable: true,
    headerClassName: "super-app-theme--header",
    sortComparator: (v1: string, v2: string) => {
      // Map severity to numeric values for proper sorting
      const severityOrder: { [key: string]: number } = {
        'high': 3,
        'medium': 2,
        'low': 1,
        '-': 0,
        '': 0
      };
      const val1 = severityOrder[(v1 || '').toLowerCase()] ?? 0;
      const val2 = severityOrder[(v2 || '').toLowerCase()] ?? 0;
      return val1 - val2;
    },
    renderCell: (params) => {
      const hcSev = params.row?.hcSeverity;
      const aePcSsSev = params.row?.aePcSsSeverity;
      const hcReason = params.row?.hcReason;
      const aePcSsReason = params.row?.aePcSsReason;

      // Build severity display text
      let severityText = "-";
      if (hcSev && aePcSsSev) {
        severityText = hcSev === aePcSsSev
          ? hcSev
          : `HC: ${hcSev}, AE/PC/SS: ${aePcSsSev}`;
      } else {
        severityText = hcSev || aePcSsSev || "-";
      }

      // Build tooltip content
      const hasReasons = hcReason || aePcSsReason;
      const tooltipContent = hasReasons ? (
        <div>
          {hcReason && (
            <div style={{ marginBottom: aePcSsReason ? '8px' : '0' }}>
              <strong>HC Reason:</strong>
              <div>{hcReason}</div>
            </div>
          )}
          {aePcSsReason && (
            <div>
              <strong>AE/PC/SS Reason:</strong>
              <div>{aePcSsReason}</div>
            </div>
          )}
        </div>
      ) : severityText;

      return (
        <Tooltip title={tooltipContent} arrow>
          <span>{severityText}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "evaluation",
    headerName: "Evaluation",
    flex: 2,
    minWidth: 200,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    renderHeader: () => <CustomEvaluationHeader />,
    renderCell: (params) => <CustomEvaluationValue data={params?.value} />,
    sortable: false,
  },
  {
    field: "reviewType",
    headerName: "Review Type",
    flex: 2,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    renderCell: (params: GridRenderCellParams) => {
      return params.row?.isSampled && params.row?.risk === "NR"
        ? Array.isArray(params.row?.sampleReviewType)
          ? params.row?.sampleReviewType.join(", ")
          : params.row?.sampleReviewType
        : params?.value;
    },
  },
  {
    field: "status",
    headerName: "Status",
    flex: 2,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    renderCell: (params: GridRenderCellParams) =>
      params.value === "-" ? (
        "-"
      ) : (
        <Chip
          label={params.value}
          variant="outlined"
          style={{
            backgroundColor: "#E6F0FF",
            color: "#000",
            fontWeight: 500,
            borderRadius: "16px",
            padding: "4px 12px",
            border: "none",
          }}
        />
      ),
  },
  {
    field: "extracted",
    headerName: "Extracted",
    flex: 2,
    minWidth: 150,
    renderCell: (params) => <span>{getFormattedDate(params.value)}</span>,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "modified",
    headerName: "Modified",
    flex: 2,
    minWidth: 150,
    renderCell: (params) => <span>{getFormattedDate(params.value)}</span>,

    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "region",
    headerName: "Region",
    flex: 1,
    minWidth: 100,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "country",
    headerName: "Country",
    flex: 1,
    minWidth: 100,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "product",
    headerName: "Product",
    flex: 2,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
];
